import React, { useEffect, useState } from "react";
import Select from "react-select";
import styles from "./Add.module.css";
import WebService from "../../Services/WebService";
import { toast } from "react-toastify";
import DocUploadPopup from "./DocUploadPopup";
import CrossButton from "../../Customs/Button/CrossButton";

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
  productToEdit?: any;
}
interface DropdownOption {
  id: number;
  name: string;
  [key: string]: any;
}
interface CustomerOption {
  value: number;
  label: string;
}

const initialForm = {
  name: "",
  prefix_id: "",
  category_id: "",
  quantity: "",
  basic_door_price: "",
  gst_percentage: 18,
  distributor_sale_discount_per: "",
  customer_sale_discount_per: "",
  is_on_sale: false,
  is_new_arrival: true, // new products start in New Arrivals; can be switched off
  is_most_selling: false,
  is_return_allowed: false,
  hsn_code: "", 
  visibility: "PUBLIC",
  prod_cust_visibility: { customer_ids: [] },
  status: "ACTIVE",
  design_description: "",
};

const requiredFields = [
  "name",
  "category_id",
  "quantity",
  "prefix_id",
  "basic_door_price",
  "gst_percentage",
];

const numericFields = [
  "quantity",
  "basic_door_price",
  "gst_percentage",
  "distributor_sale_discount_per",
  "customer_sale_discount_per",
];


const toNumber = (val: any) =>
  val !== undefined && val !== null && val !== "" ? Number(val) : undefined;

const VISIBILITY_OPTIONS = [
  { value: "PUBLIC", label: "Visible to All" },
  { value: "EXCLUDE", label: "Visible Except" },
  { value: "INCLUDE", label: "Visible Only" },
];
 
const AddProductPanel: React.FC<Props> = ({ onClose, onSuccess, productToEdit }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<any>(initialForm);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [categoryList, setCategoryList] = useState<DropdownOption[]>([]);
  const [prefixList, setPrefixList] = useState<DropdownOption[]>([]);
  const [customerList, setCustomerList] = useState<CustomerOption[]>([]);
  const [showDocPopup, setShowDocPopup] = useState(false);
  const [createdProductId, setCreatedProductId] = useState<number | null>(null);

  // Only allow numbers & decimals
  const isNumeric = (v: string) => v === "" || /^[0-9]+(\.[0-9]+)?$/.test(v);

  useEffect(() => {
    fetchDropdowns();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (productToEdit) {
      setForm({
        ...initialForm,
        ...productToEdit,
        is_new_arrival: productToEdit.is_new_arrival === true,
        gst_percentage: productToEdit.gst_percentage ?? 18,
        id: productToEdit.id,
        prod_cust_visibility: productToEdit.prod_cust_visibility || { customer_ids: [] },
        visibility: productToEdit.visibility || "PUBLIC",
         
            hsn_code:
        productToEdit.hsn_code &&
        String(productToEdit.hsn_code).trim().toLowerCase() !== "null"
          ? String(productToEdit.hsn_code)
          : "",
      });
    }
  }, [productToEdit]);

  const fetchDropdowns = async () => {
    try {
      const [prefixRes, categoryRes] = await Promise.all([
        WebService.getAPI<any>({ action: "/v1/door-management/prefix" }),
        WebService.getAPI<any>({ action: "/v1/door-management/product/categories?type=NO_SUB_CATEGORY" }),
      ]);
      setPrefixList((prefixRes.data || []).map((p: any) => ({
        id: p.prefix_id ?? p.id,
        name: p.prefix ?? p.name,
      })));
      setCategoryList((categoryRes.data || []).map((c: any) => ({
        id: c.category_id ?? c.id,
        name: c.category_name ?? c.name,
        ...c,
      })));
    } catch {
      toast.error("Failed to fetch dropdowns");
    }
  };

  const fetchCustomers = async (searchValue = "") => {
    try {
      let action = "/v1/door-management/customers?page_number=1&page_size=10";
      if (searchValue) {
        action += `&user_like=${encodeURIComponent(searchValue)}`;
      }
      const res = await WebService.getAPI<any>({ action });
      const users = res.data?.users || [];
      setCustomerList(
        users.map((c: any) => ({
          value: c.customer_id,
          label: c.company_name ? `${c.company_name} (${c.username})` : c.username,
        }))
      );
    } catch {
      toast.error("Failed to fetch customers");
    }
  };
  const handleHSNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const v = (e.target.value ?? "").toUpperCase();
  setForm((prev: any) => ({ ...prev, hsn_code: v }));
  setErrors((prev) => ({ ...prev, hsn_code: "" }));
};

  // Auto-populate price fields from selected category
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = Number(e.target.value);
    const selectedCategory = categoryList.find(cat => cat.id === selectedId);
    if (!selectedCategory) return;
    setForm((prev: any) => ({
      ...prev,
      category_id: selectedId,
      basic_door_price: selectedCategory.basic_door_price ?? "",
    }));
    setErrors((prev) => ({ ...prev, category_id: "" }));
  };

  // Numeric-only handler (blocks negative, alpha, up/down)
  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (
      value === "" ||
      (/^[0-9]+(\.[0-9]{0,2})?$/.test(value) && Number(value) >= 0)
    ) {
      setForm((prev: any) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Generic handler for string/select/textarea/checkbox
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const val = type === "checkbox" ? (e.target as HTMLInputElement).checked : value;
    setForm((prev: any) => ({ ...prev, [name]: val }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // GST % always number, default 18
  useEffect(() => {
    if (form.gst_percentage === "" || form.gst_percentage === undefined || form.gst_percentage === null) {
      setForm((prev: any) => ({ ...prev, gst_percentage: 18 }));
    }
  }, [form.gst_percentage]);

  // Visibility change
  const handleVisibilityToggle = (val: string) => {
    setForm((prev: any) => ({
      ...prev,
      visibility: val,
      prod_cust_visibility: { customer_ids: [] },
    }));
    if (val === "EXCLUDE" || val === "INCLUDE") {
      fetchCustomers("");
    }
  };

  // Customer multi-select
  const handleCustomerMultiSelect = (selected: any) => {
    setForm((prev: any) => ({
      ...prev,
      prod_cust_visibility: {
        customer_ids: selected ? selected.map((opt: any) => opt.value) : [],
      },
    }));
  };

  // Validation
  const validate = () => {
    const newErrors: { [k: string]: string } = {};
    requiredFields.forEach(field => {
      const val = form[field];
      if (val === "" || val === undefined || val === null) {
        // Name-wise error message
        let label = "";
        switch (field) {
          case "name": label = "Product Name"; break;
          case "category_id": label = "Category"; break;
          case "prefix_id": label = "Prefix"; break;
          case "quantity": label = "Stock Quantity"; break;
          case "basic_door_price": label = "Basic Door Price"; break;
          case "gst_percentage": label = "GST %"; break;
          default: label = field;
        }
        newErrors[field] = `${label} is required.`;
      } else if (numericFields.includes(field)) {
        if (!isNumeric(val)) {
          newErrors[field] = "Only positive numbers allowed.";
        } else if (Number(val) < 0) {
          newErrors[field] = "Value must be zero or greater.";
        }
      }
    });

    // Available for Sale conditional validation
    if (form.is_on_sale) {
      if (
        form.distributor_sale_discount_per === "" ||
        form.distributor_sale_discount_per === undefined ||
        form.distributor_sale_discount_per === null
      ) {
        newErrors.distributor_sale_discount_per = "Required if available for sale.";
      } else if (!isNumeric(form.distributor_sale_discount_per)) {
        newErrors.distributor_sale_discount_per = "Only positive numbers allowed.";
      } else if (Number(form.distributor_sale_discount_per) < 0) {
        newErrors.distributor_sale_discount_per = "Value must be zero or greater.";
      }
      if (
        form.customer_sale_discount_per === "" ||
        form.customer_sale_discount_per === undefined ||
        form.customer_sale_discount_per === null
      ) {
        newErrors.customer_sale_discount_per = "Required if available for sale.";
      } else if (!isNumeric(form.customer_sale_discount_per)) {
        newErrors.customer_sale_discount_per = "Only positive numbers allowed.";
      } else if (Number(form.customer_sale_discount_per) < 0) {
        newErrors.customer_sale_discount_per = "Value must be zero or greater.";
      }
    }

    if (form.name && form.name.length > 80) {
      newErrors.name = "Maximum 80 characters allowed.";
    }
    return newErrors;
  };

  // Main submit
  const handleSubmit = async () => {
    if (showDocPopup) return;
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      setLoading(true);
      const action = "/v1/door-management/product";
      let body = {
        ...form,
        prefix_id: toNumber(form.prefix_id),
        category_id: toNumber(form.category_id),
        quantity: toNumber(form.quantity),
        basic_door_price: toNumber(form.basic_door_price),
        gst_percentage: toNumber(form.gst_percentage),
        distributor_sale_discount_per: toNumber(form.distributor_sale_discount_per),
        customer_sale_discount_per: toNumber(form.customer_sale_discount_per),
        hsn_code: (form.hsn_code ?? "").trim(),
      };

      if (productToEdit) {
        if (!body.id && productToEdit.id) body.id = productToEdit.id;
        await WebService.putAPI({ action, body });
        toast.success("Product updated");
        onSuccess?.();
        onClose();
      } else {
        if (body.id) delete body.id;
        const res: any = await WebService.postAPI({ action, body });
        const createdId = res?.data?.data?.product_id || res?.data?.product_id || res?.data?.id;
        if (createdId) {
          setCreatedProductId(createdId);
          setShowDocPopup(true);
        } else {
          toast.success("Product added successfully");
          onSuccess?.();
          onClose();
        }
      }
    } catch {
      // toast.error("Failed to submit product");
    } finally {
      setLoading(false);
    }
  };

  // DocUploadPopup close
  const handleDocPopupClose = () => {
    setShowDocPopup(false);
    setCreatedProductId(null);
    onSuccess?.();
    onClose();
  };

  return (
    <>
      <div
        className={styles.overlay}
        style={showDocPopup ? { pointerEvents: "none", filter: "blur(2px)", userSelect: "none" } : {}}
        onClick={e => e.target === e.currentTarget && onClose()}
      >
        <div
          className={styles.panel}
          style={showDocPopup ? { pointerEvents: "none", opacity: 0.4, filter: "blur(1.5px)", userSelect: "none" } : {}}
        >
          <CrossButton onClick={onClose} />
          <h2>{productToEdit ? "Edit Designed Product" : "Add New Designed Material"}</h2>
          <p className={styles.intro}>Fill the product details below. All required fields are marked with *</p>
          <form
            className={styles.panelBody}
            onSubmit={e => { e.preventDefault(); if (!showDocPopup) handleSubmit(); }}
            style={showDocPopup ? { pointerEvents: "none", userSelect: "none" } : {}}
            autoComplete="off"
          >
            {/* === 1. Basic Door Details === */}
{/* === 1. Basic Door Details === */}
<div className={styles.sectionTitle}>Basic Door Details</div>
<div className={styles.sectionGroup}>
  <div>
    <label className={styles.label}>Product Name *</label>
    <input
      name="name"
      value={form.name}
      onChange={handleChange}
      className={`${styles.input} ${errors.name ? styles.inputError : ""}`}
      placeholder="Product Name"
      maxLength={80}
      autoComplete="off"
    />
  
    {errors.name && <div className={styles.error}>{errors.name}</div>}
  </div>

  <div>
    <label className={styles.label}>Category *</label>
    <select
      name="category_id"
      value={form.category_id || ""}
      onChange={handleCategoryChange}
      className={`${styles.input} ${errors.category_id ? styles.inputError : ""}`}
    >
      <option value="">Select category</option>
      {categoryList.map((cat) => (
        <option key={cat.id} value={cat.id}>{cat.name}</option>
      ))}
    </select>
    {errors.category_id && <div className={styles.error}>{errors.category_id}</div>}
  </div>

  <div>
    <label className={styles.label}>Prefix *</label>
    <select
      name="prefix_id"
      value={form.prefix_id || ""}
      onChange={handleChange}
      className={`${styles.input} ${errors.prefix_id ? styles.inputError : ""}`}
    >
      <option value="">Select prefix</option>
      {prefixList.map((prefix) => (
        <option key={prefix.id} value={prefix.id}>{prefix.name}</option>
      ))}
    </select>
    {errors.prefix_id && <div className={styles.error}>{errors.prefix_id}</div>}
  </div>

  <div>
    <label className={styles.label}>Stock Quantity *</label>
    <input
      name="quantity"
      type="text"
      value={form.quantity || ""}
      onChange={handleNumericChange}
      className={`${styles.input} ${errors.quantity ? styles.inputError : ""}`}
      readOnly={!!productToEdit}
      tabIndex={productToEdit ? -1 : 0}
      style={productToEdit ? { background: "#f2f2f2", cursor: "not-allowed" } : {}}
      placeholder="Quantity"
      autoComplete="off"
    />
    {errors.quantity && <div className={styles.error}>{errors.quantity}</div>}
  </div>

  {/* NEW: HSN Code (optional, free text) */}
  <div>
    <label className={styles.label}>
      HSN Code <span className={styles.muted}>(optional)</span>
    </label>
    <input
      name="hsn_code"
      value={form.hsn_code || ""}
      onChange={handleHSNChange}   // keep your permissive handler
      className={`${styles.input} ${errors.hsn_code ? styles.inputError : ""}`}
      placeholder="e.g., 4414 / 44.14 / ABC-123"
      maxLength={20}
      autoComplete="off"
    />
    {errors.hsn_code && <div className={styles.error}>{errors.hsn_code}</div>}
  </div>
</div>
            {/* === 2. Basic Door Price === */}
            
        <div className={styles.sectionTitle}>Basic Door Price</div>
        <div className={styles.sectionGroup}>
          
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 5 }}>
              Single Door / Back Door / Bubble Packaging *
            </div>
            <input
              name="basic_door_price"
              type="text"
              value={form.basic_door_price || ""}
              onChange={handleNumericChange}
              className={`${styles.input} ${errors.basic_door_price ? styles.inputError : ""}`}
              placeholder="₹ Basic Door Price"
              autoComplete="off"
            />
            {errors.basic_door_price && <div className={styles.error}>{errors.basic_door_price}</div>}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 5 }}>GST % *</div>
            <input
              name="gst_percentage"
              type="text"
              value={form.gst_percentage || ""}
              onChange={handleNumericChange}
              className={`${styles.input} ${errors.gst_percentage ? styles.inputError : ""}`}
              placeholder="GST %"
              autoComplete="off"
            />
            {errors.gst_percentage && <div className={styles.error}>{errors.gst_percentage}</div>}
          </div>
        </div>
            {/* === 5. Permission & Flags === */}
            <div className={styles.sectionTitle}>Permission & Flags</div>
            <div className={styles.sectionGroup}>
              <div className={styles.toggleGroup}>
                <Toggle
                  label="Available for Sale"
                  checked={form.is_on_sale}
                  onChange={val => setForm((prev: any) => ({ ...prev, is_on_sale: val }))}
                />
                <Toggle
                  label="Return Allowed"
                  checked={form.is_return_allowed}
                  onChange={val => setForm((prev: any) => ({ ...prev, is_return_allowed: val }))}
                />
                <Toggle
                  label="New Arrival"
                  checked={form.is_new_arrival}
                  onChange={val => setForm((prev: any) => ({ ...prev, is_new_arrival: val }))}
                />
                <Toggle
                  label="Trending (auto-updated monthly)"
                  checked={form.is_most_selling}
                  onChange={val => setForm((prev: any) => ({ ...prev, is_most_selling: val }))}
                />
              </div>
            </div>
            {/* Conditionally show discounts if Available for Sale is ON */}
            {form.is_on_sale && (
              <div className={styles.sectionGroup}>
                <div>
                  <label className={styles.label}>Distributor Discount (%)</label>
                  <input
                    name="distributor_sale_discount_per"
                    type="text"
                    value={form.distributor_sale_discount_per || ""}
                    onChange={handleNumericChange}
                    className={`${styles.input} ${errors.distributor_sale_discount_per ? styles.inputError : ""}`}
                    autoComplete="off"
                  />
                  {errors.distributor_sale_discount_per && (
                    <div className={styles.error}>{errors.distributor_sale_discount_per}</div>
                  )}
                </div>
                <div>
                  <label className={styles.label}>Retailer Discount (%)</label>
                  <input
                    name="customer_sale_discount_per"
                    type="text"
                    value={form.customer_sale_discount_per || ""}
                    onChange={handleNumericChange}
                    className={`${styles.input} ${errors.customer_sale_discount_per ? styles.inputError : ""}`}
                    autoComplete="off"
                  />
                  {errors.customer_sale_discount_per && (
                    <div className={styles.error}>{errors.customer_sale_discount_per}</div>
                  )}
                </div>
              </div>
            )}

            {/* === 6. Visibility Status as Toggles === */}
            <div className={styles.sectionTitle}>Visibility Status</div>
            <div className={styles.toggleBtnGroup}>
              {VISIBILITY_OPTIONS.map(opt => (
                <button
                  type="button"
                  key={opt.value}
                  className={`${styles.toggleBtn} ${form.visibility === opt.value ? styles.active : ""}`}
                  onClick={() => handleVisibilityToggle(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {(form.visibility === "EXCLUDE" || form.visibility === "INCLUDE") && (
              <div style={{ marginTop: 12 }}>
                <label style={{ fontWeight: 500, marginBottom: 3, display: "block" }}>Select Customers</label>
                <Select
                  isMulti
                  options={customerList}
                  classNamePrefix="react-select"
                  onChange={handleCustomerMultiSelect}
                  value={customerList.filter(opt =>
                    form.prod_cust_visibility.customer_ids?.includes(opt.value)
                  )}
                  placeholder="Search customers..."
                  isSearchable
                  filterOption={null}
                  onInputChange={inputValue => {
                    fetchCustomers(inputValue);
                    return inputValue;
                  }}
                />
              </div>
            )}

            {/* === 7. Description - LAST FIELD === */}
            <div className={styles.sectionTitle}>Description</div>
            <div>
              <label className={styles.label}>Product Description</label>
              <textarea
                name="design_description"
                value={form.design_description || ""}
                onChange={handleChange}
                className={styles.input}
                autoComplete="off"
              />
            </div>

            <div className={styles.buttonRow}>
              <button type="button" className={styles.closeBtn} onClick={onClose} disabled={loading || showDocPopup}>Cancel</button>
              <button type="submit" className={styles.addBtn} disabled={loading || showDocPopup}>
                {loading ? (productToEdit ? "Saving..." : "Saving...") : (productToEdit ? "Save Changes" : "Next")}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showDocPopup && createdProductId && (
        <div style={{
          position: "fixed",
          zIndex: 99999,
          top: 0, left: 0, width: "100vw", height: "100vh",
          background: "rgba(0,0,0,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <DocUploadPopup
            open={showDocPopup}
            productId={createdProductId}
            onClose={handleDocPopupClose}
          />
        </div>
      )}
    </>
  );
};

// Simple Toggle Component
function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (val: boolean) => void }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 500, fontSize: 15, cursor: "pointer" }}>
      <span style={{ minWidth: 114 }}>{label}</span>
      <span
        className={styles.toggleSwitch}
        onClick={() => onChange(!checked)}
        style={{
          background: checked ? "#FFD400" : "#ccc",
          borderRadius: 16,
          width: 36,
          height: 20,
          position: "relative",
          display: "inline-block",
          transition: "background .18s"
        }}
      >
        <span style={{
          position: "absolute",
          left: checked ? 18 : 2,
          top: 2,
          width: 16, height: 16,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          transition: "left .18s"
        }} />
      </span>
    </label>
  );
}

export default AddProductPanel;
